import React from 'react';
import { Loader, Table } from 'semantic-ui-react';

interface Dia {
    readonly cursos: readonly React.ReactElement[];
}

export interface BloqueHorario {
    readonly nombre: string;
    readonly dias: readonly Dia[];
}

const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

interface BloqueHorarioDTO {
    horaInicio: string;
    horaTermino: string;
    tipo: 'normal' | 'vespertino';
}

interface ConBloqueYDia {
    bloqueHorario: BloqueHorarioDTO;
    dia: number;
}

type Props = Readonly<{
    bloquesHorarios: readonly BloqueHorario[];
    cargando?: boolean;
    anchoCompleto?: boolean;
}>;

export const TablaDeCursos = (props: Props) => {
    const { bloquesHorarios, cargando, anchoCompleto } = props;

    const style: React.CSSProperties = {
        left: '0px',
        marginTop: '1em',
        position: anchoCompleto ? 'absolute' : 'relative',
    };

    return (
        <div style={style}>
            <Table celled definition fixed compact='very'>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell width='1'>
                            <Loader active={cargando} inline='centered' />
                        </Table.HeaderCell>

                        {HeaderDias}
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {bloquesHorarios.map(bloqueHorario => (
                        <Table.Row key={bloqueHorario.nombre} verticalAlign='top'>
                            <Table.Cell verticalAlign='middle' textAlign='center'>
                                <HeaderBloqueHorario
                                    nombreDelBloqueHorario={bloqueHorario.nombre}
                                />
                            </Table.Cell>

                            {bloqueHorario.dias.map((dia, id) => (
                                <Table.Cell key={id} width='3'>
                                    {dia.cursos}
                                </Table.Cell>
                            ))}
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </div>
    );
};

const HeaderDias = DIAS.map(dia => (
    <Table.HeaderCell width='3' key={dia} textAlign='center'>
        {dia}
    </Table.HeaderCell>
));

const HeaderBloqueHorario = (props: { nombreDelBloqueHorario: string }) => {
    const [inicio, termino] = props.nombreDelBloqueHorario.split('-');
    return (
        <>
            {inicio}
            <br />
            a
            <br />
            {termino}
        </>
    );
};

/**
 * Construye un arreglo de bloques horarios para la tabla a partir de un arreglo de cursos.
 *
 * El argumento debe ser un arreglo de objetos de tipo T con al menos los atributos bloqueHorario
 * de tipo `{ horaInicio: string; horaTermino: string; tipo: 'normal' | 'vespertino' }` y dia
 * de tipo `number`. El segundo argumento debe ser una función que tome un argumento de tipo T y
 * retorne un elemento de React. Esta función será llamada por cada elemento del arreglo a fin de
 * determinar cómo se renderizará ese determinado curso. El valor de retorno será un arreglo
 * listo para pasarlo como prop al componente `Tabla`.
 *
 * IMPORTANTE: El arreglo DEBE estar ordenado por la propiedad bloqueHorario.horaInicio. Es decir,
 * todos los bloques horarios iguales deben estar juntos y el orden de ellos determinará el orden
 * de las filas en la estructura de BloquesHorarios<U> retornada.
 */
export function construirBloquesHorarios<T extends ConBloqueYDia>(
    cursos: readonly T[],
    renderFn: (curso: T) => React.ReactElement,
): BloqueHorario[] {
    const bloquesHorarios: Array<{
        nombre: string;
        dias: Array<{ cursos: Array<React.ReactElement> }>;
    }> = [];

    let ultimoNombreDeBloqueHorarioVisto = '';
    for (const curso of cursos) {
        if (nombreDeBloqueHorario(curso.bloqueHorario) !== ultimoNombreDeBloqueHorarioVisto) {
            // Si hay un nuevo bloqueHorario, bien porque es el pimero o porque el último que se
            // había visto cambió, se debe crear un nuevo bloque horario en el arreglo. Esto
            // implica crear len(DIAS) - 1 días, cada uno con un arreglo vacío de cursos.
            bloquesHorarios.push({
                nombre: nombreDeBloqueHorario(curso.bloqueHorario),
                dias: DIAS.map(() => ({ cursos: [] })),
            });
            ultimoNombreDeBloqueHorarioVisto = nombreDeBloqueHorario(curso.bloqueHorario);
        }

        const ultimoBloqueHorario = bloquesHorarios[bloquesHorarios.length - 1];

        // Transforma el elemento del arreglo en otro de tipo CursoProps. Si la función
        // transformadora no fue provista significa que el objeto es en sí mismo asignable al
        // tipo CursoProps (ver las sobrecargas) así como solo hacemos un type assertion.
        const cursoElement = renderFn(curso);

        // ultimoBloqueHorario.dias es un arreglo de longitud len(DIAS) - 1. Como curso.dia es
        // un número entre 1 y (len(DIAS) - 1) (de lunes a viernes en general), podemos
        // referenciar ese día de ese bloque horario como curso.dia - 1, dado que bloqueHorario
        // ya fue inicializado antes con la cantidadnecesaria de días por bloque horario.
        ultimoBloqueHorario.dias[curso.dia - 1].cursos.push(cursoElement);
    }

    return bloquesHorarios;
}

function nombreDeBloqueHorario(bloqueHorario: BloqueHorarioDTO) {
    return `${bloqueHorario.horaInicio}-${bloqueHorario.horaTermino}`;
}



// WEBPACK FOOTER //
// ./src/componentes/tablasDeCursos/index.tsx